/**
 * Buyer-Demand Phase 4 — admin stats panel.
 *
 * Read-only counterpart to QuotaProgress. Renders below QuotaProgress
 * in the admin project form. Five-card funnel + conversion-rate badge
 * + 14-day completions bar chart + median completion time.
 *
 * Auto-refreshes every 15s while the project is LIVE — tighter than
 * QuotaProgress (30s) because the in-flight count is more volatile.
 * Manual Refresh button forces an immediate refetch regardless of state.
 *
 * Backed by GET /api/admin/manual-projects/:id/stats (commit c8f43ad).
 * See docs/investigations/buyer-demand-phase4-prep-2026-05-07.md §2.6.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { RefreshCw } from 'lucide-react';
import { fetchProjectStats, type ProjectStats } from '../lib/adminApi';

interface Props {
  surveyId: string;
  isLive: boolean;
}

export default function StatsPanel({ surveyId, isLive }: Props) {
  const query = useQuery({
    queryKey: ['admin', 'manual-project', surveyId, 'stats'],
    queryFn: () => fetchProjectStats(surveyId),
    refetchInterval: isLive ? 15_000 : false,
    enabled: !!surveyId,
  });

  return (
    <div className="bg-slate-800 rounded-xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-bold text-white">Project stats</p>
          <p className="text-xs text-slate-400 mt-1">
            Funnel + completion velocity. Updates every 15 seconds while the project is live.
          </p>
        </div>
        <button
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
          className="inline-flex items-center gap-1 text-xs text-slate-300 hover:text-white px-2 py-1 rounded hover:bg-slate-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${query.isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {query.isLoading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : query.isError ? (
        <p className="text-sm text-red-400">
          {query.error instanceof Error ? query.error.message : 'Failed to load stats'}
        </p>
      ) : query.data ? (
        <StatsContent stats={query.data} />
      ) : null}
    </div>
  );
}

function StatsContent({ stats }: { stats: ProjectStats }) {
  return (
    <div className="space-y-4">
      <FunnelRow stats={stats} />
      <ConversionBadge rate={stats.conversion_rate} />
      <CompletionsChart series={stats.completions_by_day} />
      <MedianStat minutes={stats.median_completion_minutes} />
    </div>
  );
}

// ── Funnel cards ────────────────────────────────────────────────────────────

function FunnelRow({ stats }: { stats: ProjectStats }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
      <MetricCard
        label="In-flight"
        value={stats.in_flight}
        // Amber when there's any in-flight session — admin should see
        // there's live attention on the survey at a glance.
        accent={stats.in_flight > 0 ? 'amber' : 'neutral'}
      />
      <MetricCard label="Starts" value={stats.total_starts} />
      <MetricCard label="Completes" value={stats.total_completes} />
      <MetricCard label="Quota full" value={stats.total_quota_full} />
      <MetricCard label="Screener fail" value={stats.total_screener_fail} />
    </div>
  );
}

function MetricCard({
  label,
  value,
  accent = 'neutral',
}: {
  label: string;
  value: number;
  accent?: 'neutral' | 'amber';
}) {
  const accentCls = accent === 'amber' ? 'text-amber-400' : 'text-white';
  return (
    <div className="bg-slate-900 rounded-lg p-3 border border-slate-700">
      <p className={`text-xl font-bold tabular-nums ${accentCls}`}>{value.toLocaleString()}</p>
      <p className="text-[11px] uppercase tracking-wide text-slate-500 mt-1">{label}</p>
    </div>
  );
}

// ── Conversion badge ────────────────────────────────────────────────────────

function ConversionBadge({ rate }: { rate: number }) {
  // Green ≥ 20%, amber 10–20%, red < 10%. Mirrors the per-cell
  // feasibility thresholds in the QuotaBuilder so admins see the
  // same colour grammar across panels.
  let cls: string;
  if (rate >= 20) cls = 'bg-green-500/15 text-green-300 border-green-500/30';
  else if (rate >= 10) cls = 'bg-amber-500/15 text-amber-300 border-amber-500/30';
  else cls = 'bg-red-500/15 text-red-300 border-red-500/30';

  return (
    <div
      className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium ${cls}`}
    >
      <span className="tabular-nums">{rate.toFixed(1)}%</span>
      <span className="text-xs opacity-80">conversion rate</span>
    </div>
  );
}

// ── Completions chart ───────────────────────────────────────────────────────

function CompletionsChart({ series }: { series: ProjectStats['completions_by_day'] }) {
  const filled = useMemo(() => fillLast14Days(series), [series]);
  const hasAny = series.length > 0;

  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Completions (last 14 days)</p>
      {!hasAny ? (
        <div className="bg-slate-900 rounded-lg border border-slate-700 h-40 flex items-center justify-center">
          <p className="text-sm text-slate-500 italic">No completions yet</p>
        </div>
      ) : (
        <div className="bg-slate-900 rounded-lg border border-slate-700 p-2" style={{ height: 160 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={filled} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <XAxis
                dataKey="date"
                stroke="#64748b"
                fontSize={10}
                tickFormatter={shortDate}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="#64748b"
                fontSize={10}
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                width={30}
              />
              <Tooltip
                contentStyle={{
                  background: '#0f172a',
                  border: '1px solid #334155',
                  borderRadius: 8,
                  fontSize: 12,
                }}
                labelStyle={{ color: '#cbd5e1' }}
                itemStyle={{ color: '#a5b4fc' }}
              />
              <Bar dataKey="count" fill="#6366f1" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ── Median ──────────────────────────────────────────────────────────────────

function MedianStat({ minutes }: { minutes: number | null }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-sm font-bold text-white tabular-nums">
        {minutes != null ? `${minutes.toFixed(1)} min` : '—'}
      </span>
      <span className="text-xs text-slate-500">median completion time</span>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Fill the last 14 days (UTC) with zero counts where the API returned
 * nothing, so the bar chart shows continuous dates rather than collapsing
 * gaps. The API window is 30 days on the server but we only chart 14 here
 * for visual density — the larger window is still available in the JSON
 * for callers that need it.
 */
function fillLast14Days(series: ProjectStats['completions_by_day']): Array<{ date: string; count: number }> {
  const byDate = new Map(series.map((r) => [r.date, r.count]));
  const out: Array<{ date: string; count: number }> = [];
  const today = new Date();
  // Today (UTC) at start-of-day, walking backwards.
  for (let i = 13; i >= 0; i--) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i));
    const iso = d.toISOString().slice(0, 10); // YYYY-MM-DD
    out.push({ date: iso, count: byDate.get(iso) ?? 0 });
  }
  return out;
}

function shortDate(iso: string): string {
  // Render YYYY-MM-DD as "May 7" — the chart axis is too narrow for full ISO.
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}
